import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import {
  PROPOSAL_BUNDLE_CATEGORY,
  PROPOSAL_BUNDLE_STATE,
  type ProposalBundle,
  type ProposalItem
} from '../api/proposalBundles';
import { formatRouteDate } from '../utils/routeInfoFormatter';
import { formatProposalPrice } from '../utils/priceFormatter';

interface Props {
  bundles: ProposalBundle[];
  includeTax?: boolean;
}
// design仮。決まったら清書
const getCategoryLabel = (category: ProposalBundle['category']): string => {
  switch (category) {
    case PROPOSAL_BUNDLE_CATEGORY.OUTWARD:
      return '行き';
    case PROPOSAL_BUNDLE_CATEGORY.HOMEWARD:
      return '帰り';
    case PROPOSAL_BUNDLE_CATEGORY.TRANSPORT:
      return '交通';
    case PROPOSAL_BUNDLE_CATEGORY.HOTEL:
      return 'ホテル';
    case PROPOSAL_BUNDLE_CATEGORY.PACKAGE:
      return 'パッケージ';
    default:
      return category;
  }
};

const getStateLabel = (state: ProposalBundle['state']): { text: string; color: string } => {
  switch (state) {
    case PROPOSAL_BUNDLE_STATE.SUGGESTING:
      return { text: '提案中', color: '#d4a60d' };
    case PROPOSAL_BUNDLE_STATE.SELECTED:
      return { text: '選択済み', color: '#4a9464' };
    case PROPOSAL_BUNDLE_STATE.REJECTED:
      return { text: '予約しない', color: '#888888' };
    default:
      return { text: state, color: '#666' };
  }
};

const getSelectedItem = (bundle: ProposalBundle): ProposalItem | null => {
  return bundle.items.find(item => item.selected) || null;
};

const LatestProposals = ({ bundles, includeTax = false }: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const hotelTotal = (
    item: ProposalItem,
    stayDays: number | null
  ): { price: number | null; priceWithTax: number | null } | null => {
    if (!stayDays || stayDays <= 0) return null;
    const price = item.price !== null ? item.price * stayDays : null;
    const priceWithTax = item.priceWithTax !== null ? item.priceWithTax * stayDays : null;
    return { price, priceWithTax };
  };

  if (bundles.length === 0) {
    return <p style={styles.placeholder}>提案内容がまだありません</p>;
  }

  return (
    <div style={styles.container}>
      {bundles.map(bundle => {
        const stateInfo = getStateLabel(bundle.state);
        const isHotel = bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOTEL;
        const checkinStr = formatRouteDate(bundle.departureDate);
        const checkoutStr = formatRouteDate(bundle.returnDate);
        const dateInfoText = (() => {
          if (isHotel && checkinStr && checkoutStr) return `${checkinStr}〜${checkoutStr}`;
          if (isHotel && checkinStr) return checkinStr;
          if (!isHotel && checkinStr) return checkinStr;
          return null;
        })();
        const stayDays = isHotel ? bundle.stayDays ?? null : null;
        const routeInfo = bundle.origin && bundle.destination ? `${bundle.origin} → ${bundle.destination}` : null;
        const selectedItem = getSelectedItem(bundle);
        const hotelStayDaysForItem = isHotel ? bundle.stayDays ?? selectedItem?.stayDays ?? null : null;
        const totalUnit = selectedItem ? hotelTotal(selectedItem, hotelStayDaysForItem) : null;

        return (
          <div key={bundle.id} style={styles.bundleCard}>
            <div style={styles.bundleHeader}>
              <span style={styles.categoryLabel}>{getCategoryLabel(bundle.category)}</span>
              <span style={{ ...styles.stateLabel, color: stateInfo.color }}>{stateInfo.text}</span>
            </div>
            {(routeInfo || dateInfoText) && (
              <div style={styles.routeInfo}>
                {routeInfo && <span>{routeInfo}</span>}
                {dateInfoText && <span style={styles.dateText}>（{dateInfoText}）</span>}
                {stayDays && stayDays > 0 && (
                  <span style={styles.dateText}>
                    {' '}
                    / {stayDays}泊{stayDays + 1}日
                  </span>
                )}
              </div>
            )}
            {selectedItem ? (
              <div style={styles.selectedItemInfo}>
                <div style={styles.selectedItemTitle}>
                  {selectedItem.flightName || selectedItem.title || '-'}
                  {selectedItem.seatClass && <span style={styles.seatClass}>（{selectedItem.seatClass}）</span>}
                </div>
                {selectedItem.departureTime && selectedItem.arrivalTime && (
                  <div style={styles.selectedItemTime}>
                    {selectedItem.departureTime} - {selectedItem.arrivalTime}
                  </div>
                )}
                <div style={styles.selectedItemPrice}>
                  {formatProposalPrice({
                    price: totalUnit ? totalUnit.price : selectedItem.price,
                    priceWithTax: totalUnit ? totalUnit.priceWithTax : selectedItem.priceWithTax,
                    includeTax
                  })}
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    marginTop: '12px'
  },
  placeholder: {
    textAlign: 'center' as const,
    color: theme.grayTextColor,
    margin: '16px 0 0',
    fontSize: '13px'
  },
  bundleCard: {
    padding: '10px 12px',
    backgroundColor: theme.contentBgColor,
    borderRadius: '6px',
    border: `1px solid ${theme.grayBorderColor}`
  },
  bundleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '4px'
  },
  categoryLabel: {
    fontSize: '13px',
    fontWeight: 600 as const,
    flex: 1
  },
  stateLabel: {
    fontSize: '11px',
    fontWeight: 500 as const
  },
  routeInfo: {
    fontSize: '12px',
    color: theme.grayTextColor,
    marginBottom: '4px'
  },
  dateText: {
    color: theme.grayTextColor
  },
  itemCount: {
    fontSize: '11px',
    color: theme.grayTextColor
  },
  selectedItemInfo: {
    marginTop: '6px',
    padding: '8px',
    backgroundColor: '#e8f5e9',
    borderRadius: '4px'
  },
  selectedItemTitle: {
    fontSize: '13px',
    fontWeight: 600 as const,
    color: theme.textColor
  },
  seatClass: {
    fontSize: '12px',
    fontWeight: 400 as const
  },
  selectedItemTime: {
    fontSize: '12px',
    color: theme.grayTextColor,
    marginTop: '2px'
  },
  selectedItemPrice: {
    fontSize: '13px',
    fontWeight: 600 as const,
    color: theme.linkColor,
    marginTop: '4px'
  }
});

export default LatestProposals;
